/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Shared contract module for the builder artifact lifecycle.
 *
 * The backend `BuilderPlanArtifact` owns the lifecycle (DRAFTING -> ... -> APPLIED
 * plus failure/superseded states) and streams `builder_lifecycle` events plus
 * `builder_plan` hydration snapshots over the agent chat WebSocket. This module
 * mirrors that wire contract (`builder_lifecycle_event_v1`), holds the single
 * metadata table every UI surface uses for lifecycle semantics, and centralizes
 * the operation labels shared by the live lifecycle view and the saved-plan
 * views (extracted from aiBuilderPlanStatus.ts so both render identically).
 */

type JsonRecord = Record<string, any>;

// ---------------------------------------------------------------------------
// Wire contract: statuses
// ---------------------------------------------------------------------------

export const BUILDER_LIFECYCLE_EVENT_TYPE = 'builder_lifecycle';
export const BUILDER_LIFECYCLE_SCHEMA_VERSION = 'builder_lifecycle_event_v1';

/** Exactly the `BuilderPlanArtifact.STATUS_*` choices from the backend contract. */
export const BUILDER_LIFECYCLE_STATUSES = [
  'DRAFTING',
  'NEEDS_REPAIR',
  'NEEDS_TOOL_RESOLUTION',
  'NEEDS_USER_CLARIFICATION',
  'READY_FOR_REVIEW',
  'AWAITING_APPROVAL',
  'APPROVED',
  'APPLYING',
  'APPLYING_RETRYABLE',
  'APPLIED',
  'FAILED_VALIDATION',
  'FAILED_APPLY',
  'SUPERSEDED',
  'ABANDONED',
] as const;

export type BuilderLifecycleStatus = (typeof BUILDER_LIFECYCLE_STATUSES)[number];

const BUILDER_LIFECYCLE_STATUS_SET = new Set<string>(BUILDER_LIFECYCLE_STATUSES);

export function isBuilderLifecycleStatus(value: unknown): value is BuilderLifecycleStatus {
  return typeof value === 'string' && BUILDER_LIFECYCLE_STATUS_SET.has(value);
}

/** Statuses that hold the lifecycle because of a blocker (NEEDS_* / FAILED_*). */
export function isBlockedLifecycleStatus(status: BuilderLifecycleStatus): boolean {
  return status.startsWith('NEEDS_') || status.startsWith('FAILED_');
}

// ---------------------------------------------------------------------------
// Status metadata table (single source of truth for UI semantics)
// ---------------------------------------------------------------------------

export type BuilderLifecycleTone = 'info' | 'success' | 'warning' | 'error';

export interface BuilderLifecycleStatusMetadata {
  /** User-facing label. */
  label: string;
  /** Visual tone for badges/alerts. */
  tone: BuilderLifecycleTone;
  /** Stepper order on the main happy path; sub/failure states share the order
   *  of the main step they interrupt. */
  order: number;
  /** Whether the artifact lifecycle has reached a resting state for this status. */
  isTerminal: boolean;
  /** Short human hint about what happens next / what the user can do. */
  actionHint: string | null;
  /** Whether the user is expected to act (answer, approve, retry). */
  requiresUserAction: boolean;
}

export const BUILDER_LIFECYCLE_STATUS_METADATA: Record<BuilderLifecycleStatus, BuilderLifecycleStatusMetadata> = {
  DRAFTING: {
    label: 'Drafting plan',
    tone: 'info',
    order: 0,
    isTerminal: false,
    actionHint: 'The builder is preparing the saved build plan.',
    requiresUserAction: false,
  },
  NEEDS_REPAIR: {
    label: 'Needs repair',
    tone: 'warning',
    order: 0,
    isTerminal: false,
    actionHint: 'The builder must repair the saved plan before it can be reviewed.',
    requiresUserAction: false,
  },
  NEEDS_TOOL_RESOLUTION: {
    label: 'Needs tool resolution',
    tone: 'warning',
    order: 0,
    isTerminal: false,
    actionHint: 'The builder is resolving a blocked build tool.',
    requiresUserAction: false,
  },
  NEEDS_USER_CLARIFICATION: {
    label: 'Needs your clarification',
    tone: 'warning',
    order: 0,
    isTerminal: false,
    actionHint: 'Answer the builder question in chat so drafting can continue.',
    requiresUserAction: true,
  },
  READY_FOR_REVIEW: {
    label: 'Ready for review',
    tone: 'info',
    order: 1,
    isTerminal: false,
    actionHint: 'The saved plan is being finalized for review.',
    requiresUserAction: false,
  },
  AWAITING_APPROVAL: {
    label: 'Awaiting approval',
    tone: 'info',
    order: 2,
    isTerminal: false,
    actionHint: 'Approve in the review panel.',
    requiresUserAction: true,
  },
  APPROVED: {
    label: 'Approved',
    tone: 'success',
    order: 3,
    isTerminal: false,
    actionHint: 'The saved plan is approved and waiting for apply.',
    requiresUserAction: false,
  },
  APPLYING: {
    label: 'Applying',
    tone: 'info',
    order: 4,
    isTerminal: false,
    actionHint: 'Applying the approved operations to your system.',
    requiresUserAction: false,
  },
  APPLYING_RETRYABLE: {
    label: 'Apply retry available',
    tone: 'warning',
    order: 4,
    isTerminal: false,
    actionHint: 'An apply operation failed but can be retried.',
    requiresUserAction: true,
  },
  APPLIED: {
    label: 'Applied',
    tone: 'success',
    order: 5,
    isTerminal: true,
    actionHint: 'The saved plan was applied and verified.',
    requiresUserAction: false,
  },
  FAILED_VALIDATION: {
    label: 'Validation failed',
    tone: 'error',
    order: 1,
    isTerminal: true,
    actionHint: 'The saved plan failed validation and needs a repair pass.',
    requiresUserAction: true,
  },
  FAILED_APPLY: {
    label: 'Apply failed',
    tone: 'error',
    order: 4,
    isTerminal: true,
    actionHint: 'Apply stopped before completion. Ask the builder to fix the blocker and retry.',
    requiresUserAction: true,
  },
  SUPERSEDED: {
    label: 'Superseded',
    tone: 'info',
    order: 2,
    isTerminal: true,
    actionHint: 'A newer plan revision replaced this one.',
    requiresUserAction: false,
  },
  ABANDONED: {
    label: 'Abandoned',
    tone: 'warning',
    order: 2,
    isTerminal: true,
    actionHint: 'This saved plan was abandoned.',
    requiresUserAction: false,
  },
};

/** Main-path stepper order used by lifecycle progress UIs. */
export const BUILDER_LIFECYCLE_STEPPER: BuilderLifecycleStatus[] = [
  'DRAFTING',
  'READY_FOR_REVIEW',
  'AWAITING_APPROVAL',
  'APPROVED',
  'APPLYING',
  'APPLIED',
];

// ---------------------------------------------------------------------------
// Wire payloads
// ---------------------------------------------------------------------------

export interface BuilderLifecycleBlocker {
  code: string | null;
  message: string | null;
}

export interface BuilderLifecycleOperation {
  operationId: string | null;
  toolName: string | null;
  mutation: string | null;
  symbolicKey: string | null;
  success: boolean;
  error: string | null;
}

export interface BuilderLifecycleOperationCounts {
  total: number;
  completed: number;
  failed: number;
}

export interface BuilderLifecycleEvent {
  type: typeof BUILDER_LIFECYCLE_EVENT_TYPE;
  schemaVersion: string;
  executionId: string | null;
  sessionId: string | null;
  agentId: number | null;
  artifactId: string | null;
  planId: string | null;
  previousStatus: BuilderLifecycleStatus | null;
  status: BuilderLifecycleStatus;
  promptPosture: string | null;
  renderedRevision: number | null;
  operations: BuilderLifecycleOperationCounts;
  lastOperation: BuilderLifecycleOperation | null;
  blockers: BuilderLifecycleBlocker[];
  detail: string | null;
  occurredAt: string;
  occurredAtMs: number;
}

export interface BuilderLifecycleSnapshotContext {
  source: 'execution_started' | 'connected' | 'subscribed';
  executionId: string | null;
  sessionId: string | null;
}

export interface BuilderPlanSnapshot {
  artifactId: string | null;
  planId: string | null;
  status: BuilderLifecycleStatus;
  renderedRevision: number | null;
  operations: BuilderLifecycleOperationCounts;
  blockers: BuilderLifecycleBlocker[];
  promptPosture: string | null;
  executionId: string | null;
  sessionId: string | null;
  occurredAt: string;
}

// ---------------------------------------------------------------------------
// Small shared JSON helpers (extracted from aiBuilderPlanStatus.ts)
// ---------------------------------------------------------------------------

export function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? { ...(value as JsonRecord) }
    : {};
}

export function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function formatTokenLabel(value: unknown): string {
  const token = asText(value);
  if (!token) return '';
  const normalized = token.replace(/[_-]+/g, ' ').trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export const BUILDER_MUTATION_LABELS: Record<string, string> = {
  create_ai_system: 'Create system',
  update_ai_system: 'Update system',
  create_expert: 'Create experts',
  update_expert: 'Update experts',
  update_expert_prompt: 'Update expert prompts',
  update_expert_model: 'Update expert models',
  update_expert_inputs: 'Update expert inputs',
  create_shared_input: 'Create shared inputs',
  update_shared_input: 'Update shared inputs',
  create_indicator_set: 'Create indicator sets',
  update_indicator_set: 'Update indicator sets',
  wire_connections: 'Wire connections',
  connect_experts: 'Wire connections',
  configure_schedules: 'Configure schedules',
  create_schedule: 'Create schedules',
  update_schedule: 'Update schedules',
  delete_schedule: 'Delete schedules',
};

export function formatMutationLabel(value: unknown): string {
  const token = asText(value);
  if (!token) return '';
  if (BUILDER_MUTATION_LABELS[token]) {
    return BUILDER_MUTATION_LABELS[token];
  }
  return formatTokenLabel(token);
}

/**
 * Label a single compiled builder operation. Shared by the live lifecycle
 * journal and the saved-plan preview so both render identically.
 */
export function summarizeBlueprintCompileOperation(item: JsonRecord): string {
  const mutation = asText(item.mutation);
  const toolName = asText(item.tool_name ?? item.toolName);
  const symbolicKey = asText(item.symbolic_key ?? item.symbolicKey);

  if (toolName === 'create_shared_input') {
    return symbolicKey ? `Create shared input: ${symbolicKey}` : 'Create shared input';
  }
  if (toolName === 'create_expert') {
    return symbolicKey ? `Create expert: ${symbolicKey}` : 'Create expert';
  }
  if (toolName === 'update_ai_system' && mutation === 'graph_wire') {
    return 'Wire system graph';
  }

  const label = formatMutationLabel(mutation || toolName);
  if (label && symbolicKey) {
    return `${label}: ${symbolicKey}`;
  }
  return label;
}

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

function toIsoText(value: unknown): string {
  const text = asText(value);
  if (!text) return '';
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? text : '';
}

function toEpochMs(isoText: string): number {
  if (!isoText) return 0;
  const parsed = Date.parse(isoText);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asNonNegativeInt(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function normalizeBlockers(value: unknown): BuilderLifecycleBlocker[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      const record = asRecord(item);
      const code = asText(record.code);
      const message = asText(record.message);
      if (!code && !message) {
        return null;
      }
      return { code: code || null, message: message || null };
    })
    .filter(Boolean) as BuilderLifecycleBlocker[];
}

function normalizeOperationCounts(value: unknown): BuilderLifecycleOperationCounts {
  const record = asRecord(value);
  return {
    total: asNonNegativeInt(record.total),
    completed: asNonNegativeInt(record.completed),
    failed: asNonNegativeInt(record.failed),
  };
}

function normalizeOperation(value: unknown): BuilderLifecycleOperation | null {
  const record = asRecord(value);
  const operationId = asText(record.operation_id ?? record.operationId);
  const toolName = asText(record.tool_name ?? record.toolName);
  const mutation = asText(record.mutation);
  const symbolicKey = asText(record.symbolic_key ?? record.symbolicKey);
  const error = asText(record.error) || null;

  if (!operationId && !toolName && !mutation && !symbolicKey) {
    return null;
  }

  return {
    operationId: operationId || null,
    toolName: toolName || null,
    mutation: mutation || null,
    symbolicKey: symbolicKey || null,
    success: record.success !== false,
    error,
  };
}

/**
 * Parse a raw `builder_lifecycle` WebSocket payload into the normalized event
 * shape used by the reducer. Returns null for payloads that do not carry a
 * recognized contract status (unknown types must never reach progress state).
 */
export function normalizeBuilderLifecycleEvent(value: unknown): BuilderLifecycleEvent | null {
  const record = asRecord(value);
  if (Object.keys(record).length === 0) {
    return null;
  }

  const status = asText(record.status);
  if (!isBuilderLifecycleStatus(status)) {
    return null;
  }

  const occurredAt = toIsoText(record.occurred_at ?? record.occurredAt);
  const revisionRaw = record.rendered_revision ?? record.renderedRevision;
  const revision =
    revisionRaw === null || revisionRaw === undefined || revisionRaw === ''
      ? null
      : asNonNegativeInt(revisionRaw);

  return {
    type: BUILDER_LIFECYCLE_EVENT_TYPE,
    schemaVersion: asText(record.schema_version ?? record.schemaVersion) || BUILDER_LIFECYCLE_SCHEMA_VERSION,
    executionId: asText(record.execution_id ?? record.executionId) || null,
    sessionId: asText(record.session_id ?? record.sessionId) || null,
    agentId: Number.isFinite(Number(record.agent_id ?? record.agentId))
      ? Number(record.agent_id ?? record.agentId)
      : null,
    artifactId: asText(record.artifact_id ?? record.artifactId) || null,
    planId: asText(record.plan_id ?? record.planId) || null,
    previousStatus: isBuilderLifecycleStatus(record.previous_status ?? record.previousStatus)
      ? (record.previous_status ?? record.previousStatus)
      : null,
    status,
    promptPosture: asText(record.prompt_posture ?? record.promptPosture) || null,
    renderedRevision: revision,
    operations: normalizeOperationCounts(record.operations),
    lastOperation: normalizeOperation(record.last_operation ?? record.lastOperation),
    blockers: normalizeBlockers(record.blockers),
    detail: asText(record.detail) || null,
    occurredAt,
    occurredAtMs: toEpochMs(occurredAt),
  };
}

/**
 * Parse a raw `builder_plan` hydration snapshot (from `execution_started`,
 * `connected.running_execution`, or `subscribed` payloads) into the normalized
 * snapshot shape. Returns null when no recognized status is present.
 */
export function normalizeBuilderPlanSnapshot(
  value: unknown,
  context?: Partial<BuilderLifecycleSnapshotContext> | null
): BuilderPlanSnapshot | null {
  const record = asRecord(value);
  if (Object.keys(record).length === 0) {
    return null;
  }

  const status = asText(record.status);
  if (!isBuilderLifecycleStatus(status)) {
    return null;
  }

  const revisionRaw = record.rendered_revision ?? record.renderedRevision;
  const revision =
    revisionRaw === null || revisionRaw === undefined || revisionRaw === ''
      ? null
      : asNonNegativeInt(revisionRaw);

  return {
    artifactId: asText(record.artifact_id ?? record.artifactId) || null,
    planId: asText(record.plan_id ?? record.planId) || null,
    status,
    renderedRevision: revision,
    operations: normalizeOperationCounts(record.operations),
    blockers: normalizeBlockers(record.blockers),
    promptPosture: asText(record.prompt_posture ?? record.promptPosture) || null,
    executionId: asText(record.execution_id ?? record.executionId) || context?.executionId || null,
    sessionId: asText(record.session_id ?? record.sessionId) || context?.sessionId || null,
    occurredAt: toIsoText(record.occurred_at ?? record.occurredAt),
  };
}
